import { useState, useEffect, useCallback, useRef } from 'react'
import { Editor } from '@tiptap/react'
import { useMutation } from '@tanstack/react-query'
import {
  Sparkles,
  Minimize2,
  Maximize2,
  Check,
  Type,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  Send,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { enhancementsApi } from '@/services/api/enhancements'
import type { InlineEditAction } from '@/types/api'

// Minimum characters required to show the AI menu
const MIN_SELECTION_LENGTH = 10

// Preset actions configuration
const PRESET_ACTIONS: {
  id: InlineEditAction
  label: string
  icon: React.ComponentType<{ className?: string }>
  description: string
}[] = [
  { id: 'summarize', label: 'Summarize', icon: Minimize2, description: 'Make it shorter' },
  { id: 'expand', label: 'Expand', icon: Maximize2, description: 'Add more detail' },
  { id: 'simplify', label: 'Simplify', icon: Type, description: 'Use simpler language' },
  { id: 'fix-grammar', label: 'Fix Grammar', icon: Check, description: 'Fix errors only' },
]

interface AIBubbleMenuProps {
  editor: Editor
  articleContext: string
}

export function AIBubbleMenu({ editor, articleContext }: AIBubbleMenuProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [selectedText, setSelectedText] = useState('')
  const [customPrompt, setCustomPrompt] = useState('')
  const [error, setError] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Mutation for inline edit API call
  const inlineEditMutation = useMutation({
    mutationFn: (params: { action: InlineEditAction; customPrompt?: string }) =>
      enhancementsApi.inlineEdit({
        selectedText,
        articleContext,
        action: params.action,
        customPrompt: params.customPrompt,
      }),
    onSuccess: (data) => {
      // Replace the selection with enhanced text
      editor
        .chain()
        .focus()
        .deleteSelection()
        .insertContent(data.enhanced_text)
        .run()

      // Hide the menu
      setIsVisible(false)
      setCustomPrompt('')
      setError(null)
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to enhance text')
    },
  })

  // Handle preset action click
  const handlePresetAction = useCallback((action: InlineEditAction) => {
    setError(null)
    inlineEditMutation.mutate({ action })
  }, [inlineEditMutation])

  // Handle custom prompt submission
  const handleCustomSubmit = useCallback(() => {
    if (!customPrompt.trim()) return
    setError(null)
    inlineEditMutation.mutate({ action: 'custom', customPrompt: customPrompt.trim() })
  }, [customPrompt, inlineEditMutation])

  // Handle key press in custom input
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleCustomSubmit()
    }
    if (e.key === 'Escape') {
      setIsVisible(false)
    }
  }, [handleCustomSubmit])

  // Update menu position based on selection
  const updatePosition = useCallback(() => {
    const { from, to, empty } = editor.state.selection

    // Hide if selection is empty (just a cursor, no actual selection)
    if (empty || from === to) {
      setIsVisible(false)
      return
    }

    const text = editor.state.doc.textBetween(from, to, ' ').trim()

    // Hide if selected text is too short
    if (!text || text.length < MIN_SELECTION_LENGTH) {
      setIsVisible(false)
      return
    }

    // Get the DOM selection to find coordinates
    const domSelection = window.getSelection()
    if (!domSelection || domSelection.rangeCount === 0 || domSelection.isCollapsed) {
      setIsVisible(false)
      return
    }

    // Verify the DOM selection actually has content
    const domSelectedText = domSelection.toString().trim()
    if (!domSelectedText || domSelectedText.length < MIN_SELECTION_LENGTH) {
      setIsVisible(false)
      return
    }

    setSelectedText(text)

    const range = domSelection.getRangeAt(0)
    const rect = range.getBoundingClientRect()

    // Validate rect has actual dimensions (not a collapsed selection)
    if (rect.width === 0 || rect.height === 0) {
      setIsVisible(false)
      return
    }

    // Get editor container position
    const editorElement = editor.view.dom.closest('.tiptap-editor')?.parentElement
    if (!editorElement) {
      setIsVisible(false)
      return
    }

    const editorRect = editorElement.getBoundingClientRect()

    // Calculate position relative to the editor container
    // Position above the selection by default
    const menuWidth = 320 // Approximate menu width
    let left = rect.left - editorRect.left + (rect.width / 2) - (menuWidth / 2)
    let top = rect.top - editorRect.top - 10 // 10px gap above selection

    // Keep within bounds
    left = Math.max(8, Math.min(left, editorRect.width - menuWidth - 8))

    // If too close to top, position below
    if (top < 60) {
      top = rect.bottom - editorRect.top + 10
    }

    setPosition({ top, left })
    setIsVisible(true)
    setError(null)
  }, [editor])

  // Listen for mouseup to detect when selection is complete
  useEffect(() => {
    const editorElement = editor.view.dom

    const handleMouseUp = () => {
      // Small delay to ensure selection is finalized
      setTimeout(() => {
        updatePosition()
      }, 50)
    }

    // Hide menu when user starts a new selection (mousedown)
    const handleMouseDown = () => {
      setIsVisible(false)
    }

    // Also handle keyboard selection (Shift+Arrow keys)
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.shiftKey || e.key === 'Shift') {
        setTimeout(() => {
          updatePosition()
        }, 50)
      }
    }

    editorElement.addEventListener('mouseup', handleMouseUp)
    editorElement.addEventListener('mousedown', handleMouseDown)
    editorElement.addEventListener('keyup', handleKeyUp)

    return () => {
      editorElement.removeEventListener('mouseup', handleMouseUp)
      editorElement.removeEventListener('mousedown', handleMouseDown)
      editorElement.removeEventListener('keyup', handleKeyUp)
    }
  }, [editor, updatePosition])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Don't close if clicking in the editor (selection might change)
        const editorElement = editor.view.dom
        if (!editorElement.contains(event.target as Node)) {
          setIsVisible(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editor])

  // Don't auto-focus input - let user click if they want custom prompt
  // This keeps the text selection visible in the editor

  // Prevent menu interactions from stealing focus/deselecting text
  // But allow input elements to receive focus normally
  const handleMenuMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    // Allow input and button elements to work normally
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button')) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
  }, [])

  if (!isVisible) return null

  const isLoading = inlineEditMutation.isPending

  return (
    <div
      ref={menuRef}
      className={cn(
        'absolute z-50 bg-white rounded-lg shadow-lg border border-gray-200',
        'animate-in fade-in-0 zoom-in-95 duration-150',
        'w-[320px]',
        'select-none' // Prevent text selection in menu
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateY(-100%)',
      }}
      onMouseDown={handleMenuMouseDown}
    >
      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Enhancing text...</span>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="p-3">
          <div className="flex items-center gap-2 text-sm text-red-600 mb-2">
            <AlertCircle className="h-4 w-4" />
            <span className="flex-1">{error}</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setError(null)}
            >
              <RefreshCw className="h-3 w-3 mr-1.5" />
              Try Again
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(false)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Normal State */}
      {!isLoading && !error && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <Sparkles className="h-4 w-4 text-purple-500" />
              AI Enhance
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Preset Actions */}
          <div className="p-2 border-b">
            <div className="grid grid-cols-2 gap-1.5">
              {PRESET_ACTIONS.map((action) => {
                const Icon = action.icon
                return (
                  <button
                    key={action.id}
                    onClick={() => handlePresetAction(action.id)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-md text-left',
                      'hover:bg-purple-50 hover:text-purple-700 transition-colors',
                      'text-sm text-gray-700'
                    )}
                    title={action.description}
                  >
                    <Icon className="h-4 w-4 text-gray-500" />
                    <span>{action.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Custom Prompt */}
          <div className="p-2">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                placeholder="Custom instruction..."
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                className="h-8 text-sm"
              />
              <Button
                size="sm"
                onClick={handleCustomSubmit}
                disabled={!customPrompt.trim()}
                className="h-8 px-3"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Selection info */}
          <div className="px-3 py-1.5 bg-gray-50 text-xs text-gray-500 border-t rounded-b-lg">
            {selectedText.length} characters selected
          </div>
        </>
      )}
    </div>
  )
}

export default AIBubbleMenu
